import {
  MaterialType,
  ORDER_TYPES,
  OfferLinePrice,
  OfferResponse,
  OrderResponse,
  ServiceLine,
} from "../types";

/**
 * SCRUM-116: a service quoted by line is priced line by line — the supplier puts a unit price, in
 * the line's own currency, on each line. Lines in one currency add up to one total; lines in
 * different currencies are never converted, so the service has one total per currency. Inverse
 * auctions keep the single price: their position and decrement are per offer.
 */

/** One line's price as the form edits it: the unit price as typed, and its currency. */
export interface LinePriceInput {
  unitPrice: string;
  currency: string;
}

/** Line prices as the form edits them, by line code. */
export type LinePriceInputs = Record<string, LinePriceInput>;

export interface CurrencyTotal {
  currency: string;
  total: number;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

export const isPricedByLine = (order?: OrderResponse | null): boolean =>
  !!order &&
  order.material?.category?.type === MaterialType.SERVICES &&
  order.type !== ORDER_TYPES.INVERSE &&
  order.type !== ORDER_TYPES.INVERSE_JOINT &&
  (order.serviceLines ?? []).some((line) => !!line.code);

/** The lines a supplier prices, in the order the buyer wrote them. */
export const pricedLinesOf = (order?: OrderResponse | null): ServiceLine[] =>
  isPricedByLine(order)
    ? (order!.serviceLines ?? []).filter((line) => !!line.code)
    : [];

export const lineSubtotal = (line: ServiceLine, unitPrice: unknown): number =>
  round2((Number(unitPrice) || 0) * (Number(line.quantity) || 0));

export const isLinePriced = (prices: LinePriceInputs, code?: string) =>
  !!code && Number(prices[code]?.unitPrice) > 0;

export const allLinesPriced = (lines: ServiceLine[], prices: LinePriceInputs) =>
  lines.length > 0 && lines.every((line) => isLinePriced(prices, line.code));

/** The service's totals, one per currency, in the order the lines first use them. */
export const linePriceTotals = (
  lines: ServiceLine[],
  prices: LinePriceInputs
): CurrencyTotal[] =>
  lines.reduce((totals: CurrencyTotal[], line) => {
    const price = prices[line.code!];
    if (!price?.currency || !isLinePriced(prices, line.code)) return totals;
    const subtotal = lineSubtotal(line, price.unitPrice);
    const existing = totals.find((t) => t.currency === price.currency);
    return existing
      ? totals.map((t) =>
          t === existing ? { ...t, total: round2(t.total + subtotal) } : t
        )
      : [...totals, { currency: price.currency, total: subtotal }];
  }, []);

/** The offer's currency: its first line's, as the backend reads it. */
export const offerCurrencyOf = (
  lines: ServiceLine[],
  prices: LinePriceInputs
) => prices[lines[0]?.code ?? ""]?.currency ?? "";

/** The offer's price: the total of the lines in its currency, as the backend recomputes it. */
export const linePricesTotal = (
  lines: ServiceLine[],
  prices: LinePriceInputs
) =>
  linePriceTotals(lines, prices).find(
    (t) => t.currency === offerCurrencyOf(lines, prices)
  )?.total ?? 0;

/** Every line, unpriced, in one currency: where a new offer starts. */
export const emptyLinePrices = (
  lines: ServiceLine[],
  currency: string
): LinePriceInputs =>
  Object.fromEntries(
    lines.map((line) => [line.code!, { unitPrice: "", currency }])
  );

/** An offer's line prices, back in the shape the form edits; unpriced lines in `currency`. */
export const linePriceInputsOf = (
  lines: ServiceLine[],
  linePrices: OfferLinePrice[] | null | undefined,
  currency: string
): LinePriceInputs => ({
  ...emptyLinePrices(lines, currency),
  ...Object.fromEntries(
    (linePrices ?? []).map((p) => [
      p.lineCode,
      { unitPrice: String(p.unitPrice), currency: p.currency || currency },
    ])
  ),
});

/** What an offer sends: one unit price, in its currency, per line. */
export const linePricesPayload = (
  lines: ServiceLine[],
  prices: LinePriceInputs
): OfferLinePrice[] =>
  lines.map((line) => ({
    lineCode: line.code!,
    unitPrice: Number(prices[line.code!]?.unitPrice),
    currency: prices[line.code!]?.currency,
  }));

/** A stored offer's totals per currency, read from its lines. */
export const offerLineTotals = (
  offer?: OfferResponse | null
): CurrencyTotal[] => {
  const lines = pricedLinesOf(offer?.order);
  if (!offer || !lines.length || !offer.linePrices?.length) return [];
  return linePriceTotals(
    lines,
    linePriceInputsOf(lines, offer.linePrices, offer.currency)
  );
};

/** A price as the supplier types it: digits and one decimal point, at most two decimals. */
export const parsePriceInput = (text: string): string => {
  const cleaned = text.replace(/[^\d.]/g, "");
  const [whole, ...rest] = cleaned.split(".");
  return rest.length ? `${whole}.${rest.join("").slice(0, 2)}` : whole;
};

/** A typed price with thousands separators, keeping what the supplier is still typing. */
export const formatPriceInput = (raw: string): string => {
  if (!raw) return "";
  const [whole, decimals] = raw.split(".");
  const grouped = whole ? Number(whole).toLocaleString("en-US") : "0";
  return decimals !== undefined ? `${grouped}.${decimals}` : grouped;
};
